import React, { useState, useEffect, useRef } from 'react';
import { CloseOutlined, LeftOutlined, RightOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { formatDateVN } from '@/common/ultils';

const ImagePopupSlideshow = ({ content, fontSize = 16, listImg, mediaContent, date }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!contentRef?.current) return;

    // Function để xử lý hình ảnh
    const handleImages = () => {
      if (contentRef?.current) {
        const images = contentRef.current.querySelectorAll('img');

        images.forEach((img) => {
          img.style.cursor = 'pointer';
          img.onclick = (e) => {
            e.preventDefault();
            const indexImg = listImg.findIndex((item) => item === img.src);
            setImageUrls(listImg);
            setCurrentImageIndex(indexImg == -1 ? 0 : indexImg);
            setIsPopupOpen(true);
            setZoom(1);
            setDragOffset({ x: 0, y: 0 });
          };
        });
      }
    };

    // Xử lý hình ảnh có sẵn ngay khi component mount
    handleImages();

    // Theo dõi thay đổi DOM cho hình ảnh mới
    const observer = new MutationObserver(handleImages);

    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, [content, contentRef?.current]);

  const closePopup = () => {
    setIsPopupOpen(false);
    setZoom(1);
    setDragOffset({ x: 0, y: 0 });
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === imageUrls.length - 1 ? 0 : prev + 1));
    setZoom(1);
    setDragOffset({ x: 0, y: 0 });
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? imageUrls.length - 1 : prev - 1));
    setZoom(1);
    setDragOffset({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 0.5));
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setDragOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleKeyDown = (e) => {
    if (!isPopupOpen) return;

    switch (e.key) {
      case 'Escape':
        closePopup();
        break;
      case 'ArrowLeft':
        prevImage();
        break;
      case 'ArrowRight':
        nextImage();
        break;
      case '+':
      case '=':
        handleZoomIn();
        break;
      case '-':
        handleZoomOut();
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPopupOpen]);
  return (
    <div className="relative overflow-y-auto">
      {/* Content với ảnh có thể click */}
      {mediaContent ? (
        <div
          ref={contentRef}
          className="main-longforms mx-auto"
          style={{ fontSize: `${fontSize}px` }}
          dangerouslySetInnerHTML={{
            __html: `
            <p style="color: #768496; margin-bottom: 8px;">${date ? formatDateVN(date, 'dd/MM/yyyy - HH:mm') : ''}</p>
            ${content}
        `,
          }}
        />
      ) : (
        <div
          ref={contentRef}
          className="text-md content-articles ck ck-content ck-editor__editable ck-rounded-corners ck-editor__editable_inline ck-blurred customeimg"
          style={{ fontSize: `${fontSize}px` }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      )}
      {/* Popup Slideshow */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black/80 bg-opacity-90 flex items-center justify-center z-50">
          {/* Close Button */}
          <button onClick={closePopup} className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 cursor-pointer">
            <CloseOutlined size={32} />
          </button>

          {/* Zoom Controls */}
          <div className="absolute top-4 left-4 flex space-x-2 z-10">
            <button onClick={handleZoomOut} className="bg-black bg-opacity-50 text-white p-2 rounded hover:bg-opacity-70 cursor-pointer">
              <ZoomOutOutlined size={20} />
            </button>
            <button onClick={handleZoomIn} className="bg-black bg-opacity-50 text-white p-2 rounded hover:bg-opacity-70 cursor-pointer">
              <ZoomInOutlined size={20} />
            </button>
            <span className="bg-black bg-opacity-50 text-white px-3 py-2 rounded">{Math.round(zoom * 100)}%</span>
          </div>

          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded">
            {currentImageIndex + 1} / {imageUrls.length}
          </div>

          {/* Previous Button */}
          {imageUrls.length > 1 && (
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50
              text-white p-3 rounded-full hover:bg-opacity-70 cursor-pointer">
              <LeftOutlined size={24} />
            </button>
          )}

          {/* Next Button */}
          {imageUrls.length > 1 && (
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50
              text-white p-3 rounded-full hover:bg-opacity-70 cursor-pointer">
              <RightOutlined size={24} />
            </button>
          )}
          {/* Main Image */}
          <div className="relative max-w-[80%] max-h-[80%] overflow-hidden flex justify-center">
            <img
              src={imageUrls[currentImageIndex]}
              alt={`Slide ${currentImageIndex}`}
              className="max-w-full max-h-full object-contain cursor-grab active:cursor-grabbing"
              style={{
                transform: `scale(${zoom}) translate(${dragOffset.x / zoom}px, ${dragOffset.y / zoom}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              draggable={false}
            />
          </div>

          {/* Thumbnail Navigation */}
          {imageUrls.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 max-w-full overflow-x-auto">
              {imageUrls.map((url, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentImageIndex(index);
                    setZoom(1);
                    setDragOffset({ x: 0, y: 0 });
                  }}
                  className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden cursor-pointer ${
                    index === currentImageIndex ? 'border-white' : 'border-gray-500 hover:border-gray-300'
                  }`}>
                  <img src={url} alt={`Thumbnail ${index}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <style>
        {mediaContent
          ? `
            .main-longforms {
                width: 680px;
            }
            .small-img { width: 100%; }

            .medium-img { min-width: 980px; max-width: 980px; margin-left: -160px; }

            .large-img {
                min-width: 100vw !important;
                max-width: 100vw !important;
                position: relative;
                margin-left: -50vw !important;
                left: 50%;
                max-width: 95vw;
            }

            .box-flex {
                display: flex;
            }

            .box-img-custom {
                min-width: 980px;
                max-width: 980px;
                margin-left: -160px;
            }

            .img-left-custom {
                float: left;
                margin-left: -150px;
                margin-right: 30px;
                min-width: 450px;
                max-width: 450px;
            }

            .img-right-custom {
                float: right;
                margin-right: -150px;
                min-width: 450px;
                max-width: 450px;
                margin-left: 30px;
            }
            @media only screen and (max-width: 760px) {
                p {
                    padding: 0px 0.25rem;
                }
                .main-longforms {
                    width: 100vw !important;
                }
                .small-img { width: 100vw !important; }
                .medium-img { 
                    width: 100vw !important;
                }

                .large-img {
                    width: 100vw !important;
                }

                .img-left-custom {
                    float: left;
                    margin-right: 10px;
                    min-width: 50vw !important;
                    max-width: 50vw !important;
                    margin-left: 0px !important;
                }

                .img-right-custom {
                    float: right;
                    margin-left: 10px;
                    min-width: 50vw !important;
                    max-width: 50vw !important;
                    margin-right: 0px !important;
                }
            }
          `
          : ''}
      </style>
    </div>
  );
};

// Component demo với content mẫu
const PreViewComponent = ({ date, content, fontSize, listImg, mediaContent = false }) => {
  return <ImagePopupSlideshow date={date} content={content} fontSize={fontSize} listImg={listImg} mediaContent={mediaContent} />;
};

export default PreViewComponent;
